/* ============================================================================
   ANCONA APEX V5 — GLOBAL SHELL SCRIPT (ancona-v5-shell.js)
   Agent A (2026-07-28). The ONE global custom JS request. Classic script,
   loaded with defer from layout/theme.liquid.

   Provides (window.AnconaV5):
   - bus        event-bus helper over document CustomEvents
   - trapFocus  the single focus-trap implementation for all V5 dialogs
   - announce   polite/assertive live-region announcer
   - dialog     manager for [data-av5-dialog] roots ([data-av5-dialog-open] /
                [data-av5-dialog-close] triggers, Escape, backdrop, focus
                return, scroll lock)
   - presentationPolicy  fail-closed consumer of the server-emitted product-ID
                exclusion JSON
   - loadClientBook  deferred, non-blocking load of ancona-v5-client-book.js on
                Client Book/Compare interaction or the direct Client Book page
   - loadConcierge  deferred Section Rendering markup + CSS/script load on
                explicit launcher interaction
   - recent tracking  a shell-local, schema-bounded product-context update;
                recording a product view does not load the Client Book bundle
   - metrics    explicit-consent-gated public `ancona:*` event publication;
                Client Book UI state remains on the local event bus only

   Sticky header: native Dawn <sticky-header> (sections/header.liquid) remains
   the authority — no custom sticky logic here.
   Idempotent: guarded singleton; all wiring is delegated document-level
   listeners, so shopify:section:load re-renders need no re-binding. Header
   badge counts are refreshed on shopify:section:load per the P0-8 pattern.
   Storage-denied: every localStorage touch is inside try/catch; failures
   degrade to hidden badges and an unavailability announcement from the
   Client Book module itself.
   ============================================================================ */
(function () {
  'use strict';
  if (window.AnconaV5) return;

  var shellScript = document.currentScript;
  var SCRIPT_SRC = (shellScript && shellScript.src) || '';
  var CLIENT_BOOK_SRC = (shellScript && shellScript.dataset.av5ClientBookSrc) || '';
  var CONCIERGE_SRC = (shellScript && shellScript.dataset.av5ConciergeSrc) || '';
  var CONCIERGE_CSS = (shellScript && shellScript.dataset.av5ConciergeCss) || '';
  var TRACK_RECENT = !!(shellScript && shellScript.dataset.av5TrackRecent === 'true');
  var BOOK_KEY = 'ancona:v2:client-book';

  /* Shopify's documented consent API is loaded separately from the native banner.
     This loader records no consent and replays no events. Optional data stays
     blocked while the documented methods are missing, loading, or failing. */
  var privacyApiRequested = false;
  var PREFERENCES_DECISION_KEY = 'ancona:privacy:preferences:v1';
  var volatilePreferencesDecision = null;
  /* This minimal consent-decision record coordinates our own local history.
     It never sets Shopify consent or grants permission to another tab. */
  function preferencesDecision() {
    if (volatilePreferencesDecision !== null) return volatilePreferencesDecision;
    try {
      var raw = window.localStorage.getItem(PREFERENCES_DECISION_KEY);
      if (raw === null) return '';
      if (raw.length > 100) return 'invalid';
      var value = JSON.parse(raw);
      return value && value.v === 1 && (value.preferences === 'yes' || value.preferences === 'no')
        ? value.preferences : 'invalid';
    } catch (error) { return 'invalid'; }
  }
  function collectPreferencesDecision(event) {
    var choice = '';
    try {
      var detail = event && event.detail;
      if (detail && detail.preferencesAllowed === false) choice = 'no';
      else if (privacyApiReady()) {
        var privacy = window.Shopify.customerPrivacy;
        var consent = privacy.currentVisitorConsent();
        if (consent && consent.preferences === 'no') choice = 'no';
        else if (consent && consent.preferences === 'yes') {
          choice = privacy.preferencesProcessingAllowed() === true ? 'yes' : 'no';
        }
      }
    } catch (error) { /* No unresolved API result may create a grant. */ }
    if (!choice) return;
    var serialized = JSON.stringify({ v: 1, preferences: choice });
    volatilePreferencesDecision = choice === 'no' ? 'no' : 'invalid';
    try {
      window.localStorage.setItem(PREFERENCES_DECISION_KEY, serialized);
      volatilePreferencesDecision = null;
    } catch (error) { /* Keep this tab blocked if coordination cannot be stored. */ }
    if (choice === 'no') {
      clearStoredRecentHistory();
      /* Removing optional history can free space for the essential decision. */
      if (volatilePreferencesDecision !== null) {
        try {
          window.localStorage.setItem(PREFERENCES_DECISION_KEY, serialized);
          volatilePreferencesDecision = null;
        } catch (error) { /* The local denial and cleanup remain in force. */ }
      }
    }
  }
  function privacyApiReady() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    return !!(privacy && typeof privacy.currentVisitorConsent === 'function' &&
      typeof privacy.analyticsProcessingAllowed === 'function' &&
      typeof privacy.preferencesProcessingAllowed === 'function');
  }
  function requestPrivacyApi() {
    if (privacyApiRequested || privacyApiReady()) return;
    var shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') return;
    privacyApiRequested = true;
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error && privacyApiReady()) {
          document.dispatchEvent(new CustomEvent('ancona:privacy-ready'));
        }
      });
    } catch (error) { /* fail closed; no repeated requests or assumed permission */ }
  }
  function privacyAllows(purpose) {
    try {
      requestPrivacyApi();
      if (!privacyApiReady()) return false;
      var privacy = window.Shopify.customerPrivacy;
      var method = purpose === 'analytics' ? 'analyticsProcessingAllowed' :
        (purpose === 'preferences' ? 'preferencesProcessingAllowed' : '');
      if (!method) return false;
      if (purpose === 'preferences') {
        var decision = preferencesDecision();
        if (decision === 'no' || decision === 'invalid') return false;
      }
      var consent = privacy.currentVisitorConsent();
      return !!(consent && consent[purpose] === 'yes' && privacy[method]() === true);
    } catch (error) { return false; }
  }
  function stripRecentHistory(data) {
    if (!data || !Array.isArray(data.saved) || !Array.isArray(data.compare)) return false;
    var changed = !!(Array.isArray(data.recent) && data.recent.length);
    data.recent = [];
    var keep = Object.create(null);
    data.saved.forEach(function (entry) { if (entry && entry.h) keep[entry.h] = true; });
    data.compare.forEach(function (entry) {
      var handle = typeof entry === 'string' ? entry : entry && entry.h;
      if (handle) keep[handle] = true;
    });
    if (data.ids && typeof data.ids === 'object' && !Array.isArray(data.ids)) {
      Object.keys(data.ids).forEach(function (handle) {
        if (!keep[handle]) { delete data.ids[handle]; changed = true; }
      });
    }
    return changed;
  }
  function clearStoredRecentHistory() {
    /* An unresolved API hides history but does not erase a returning visitor's
       prior history merely because Shopify is still loading. */
    var decision = preferencesDecision();
    /* A tab with an older native denial must not erase a newer explicit grant.
       It still cannot display or record history without its own API permission. */
    if (decision === 'yes') return false;
    if (decision !== 'no' && decision !== 'invalid' &&
        (!privacyApiReady() || privacyAllows('preferences'))) return false;
    var changed = false;
    try {
      if (window.localStorage.getItem('ancona:recently-viewed') !== null) {
        window.localStorage.removeItem('ancona:recently-viewed');
        changed = true;
      }
      var raw = window.localStorage.getItem(BOOK_KEY);
      if (!raw || raw.length > 200000) return changed;
      var data = JSON.parse(raw);
      if (data && data.v === 2 && stripRecentHistory(data)) {
        window.localStorage.setItem(BOOK_KEY, JSON.stringify(data));
        changed = true;
      }
    } catch (error) { /* storage failure must not affect shopping */ }
    return changed;
  }
  window.AnconaPrivacy = {
    allows: privacyAllows,
    ready: privacyApiReady,
    requestApi: requestPrivacyApi,
    preferencesDecision: preferencesDecision,
    stripRecentHistory: stripRecentHistory,
    clearStoredRecentHistory: clearStoredRecentHistory
  };
  requestPrivacyApi();
  document.addEventListener('DOMContentLoaded', requestPrivacyApi, { once: true });
  window.addEventListener('load', requestPrivacyApi, { once: true });


  /* Customer-facing strings; localize via window.AnconaV5ShellStrings
     (inline t|json emission in layout/theme.liquid). Hidden contact-form
     field VALUES (inquiry type / context) intentionally stay English:
     they are a merchant-inbox data contract, not customer-facing text. */
  var SHELL_S = Object.assign({
    guideUnavailableContact: 'The shopping guide is unavailable right now. Opening direct contact instead.',
    bookUnavailable: 'The Client Book is unavailable right now. Shopping is unaffected.',
    prefillRequest: 'Request: ',
    prefillFacts: 'Listed facts for Ancona to verify: ',
    contextIncludes: 'Your request includes ',
    contextAnd: ' and ',
    contextComma: ', ',
    contextReview: '. Review or edit your question below; Ancona will confirm any listed facts or physical fit.',
    contextPartial: ' One or more requested product links could not be revalidated and were not attached.',
    contextFailed: 'The requested product context could not be revalidated, so no product was attached. Paste the product link into your question if you still want Ancona to review it.',
    contextReady: 'Your Concierge request is ready for review. Edit it below before sending it through Shopify’s contact form.',
    questionPlaceholder: 'What would you like Ancona to confirm or create?',
    contextError: 'The Concierge context could not be prepared automatically. Nothing was submitted; add the product link and request manually.',
    sharedBookUnavailable: 'This shared Client Book is unavailable right now. Shopping is unaffected.',
    contactResponseNoGuide: 'Your contact response is available on the page, but the shopping guide could not open.'
  }, (function (raw) {
    /* Reject Shopify "Translation missing" values so a locale gap
       falls back to the English default instead of leaking the marker. */
    var clean = {};
    for (var k in raw) {
      if (typeof raw[k] === 'string' && raw[k] && raw[k].indexOf('Translation missing') !== 0) clean[k] = raw[k];
    }
    return clean;
  })(window.AnconaV5ShellStrings || {}));

  /* ---- Presentation policy (one JSON parse, exact product IDs) ------------ */
  function cleanProductId(value) {
    var id = String(value == null ? '' : value).trim();
    return /^\d+$/.test(id) ? id : '';
  }

  var policyAvailable = false;
  var excludedProductIds = Object.create(null);
  var policyNode = document.querySelector('[data-ancona-presentation-policy]');
  if (policyNode) {
    try {
      var policyValues = JSON.parse(policyNode.textContent || '[]');
      if (Array.isArray(policyValues)) {
        policyAvailable = true;
        for (var policyIndex = 0; policyIndex < policyValues.length; policyIndex += 1) {
          var excludedId = cleanProductId(policyValues[policyIndex]);
          if (!excludedId) {
            policyAvailable = false;
            excludedProductIds = Object.create(null);
            break;
          }
          excludedProductIds[excludedId] = true;
        }
      }
    } catch (error) {
      policyAvailable = false;
      excludedProductIds = Object.create(null);
    }
  }

  function presentationDecision(productId) {
    var id = cleanProductId(productId);
    if (!policyAvailable || !id) return 'unknown';
    return excludedProductIds[id] ? 'deny' : 'allow';
  }

  var presentationPolicy = {
    available: policyAvailable,
    decision: presentationDecision,
    allows: function (productId) {
      return presentationDecision(productId) === 'allow';
    },
    denies: function (productId) {
      return presentationDecision(productId) === 'deny';
    }
  };

  function applyPresentationPolicy(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var controls = root.querySelectorAll('[data-av5-book], [data-av5-compare-toggle]');
    for (var i = 0; i < controls.length; i += 1) {
      var productId = controls[i].dataset.av5ProductId || controls[i].dataset.productId;
      if (!presentationPolicy.allows(productId)) {
        controls[i].setAttribute('hidden', '');
      }
    }
  }

  /* ---- Event bus ---------------------------------------------------------- */
  var bus = {
    on: function (name, handler) {
      document.addEventListener(name, handler);
      return function () { document.removeEventListener(name, handler); };
    },
    off: function (name, handler) {
      document.removeEventListener(name, handler);
    },
    emit: function (name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    }
  };

  /* ---- Focus trap (single implementation) --------------------------------- */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), ' +
    'summary, iframe';

  function focusables(container) {
    var list = [];
    var nodes = container.querySelectorAll(FOCUSABLE);
    for (var i = 0; i < nodes.length; i += 1) {
      var el = nodes[i];
      if (el.offsetParent !== null || el === document.activeElement) list.push(el);
    }
    return list;
  }

  function trapFocus(container, initial) {
    var target = initial || container.querySelector('[data-av5-dialog-initial]') || focusables(container)[0] || container;
    if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    target.focus();

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var items = focusables(container);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    return function release() {
      container.removeEventListener('keydown', onKeydown);
    };
  }

  /* ---- Live-region announcer ---------------------------------------------- */
  var regions = {};

  function liveRegion(tone) {
    var key = tone === 'assertive' ? 'assertive' : 'polite';
    if (!regions[key] || !regions[key].isConnected) {
      var el = document.createElement('div');
      el.className = 'av5-live-region';
      el.setAttribute('aria-live', key);
      el.setAttribute('role', key === 'assertive' ? 'alert' : 'status');
      document.body.appendChild(el);
      regions[key] = el;
    }
    return regions[key];
  }

  function announce(message, tone) {
    if (!message) return;
    var region = liveRegion(tone);
    region.textContent = '';
    window.setTimeout(function () { region.textContent = String(message); }, 40);
  }

  /* ---- Dialog manager for [data-av5-dialog] ------------------------------- */
  var dialogStack = []; /* [{ root, opener, release }] */

  function syncDialogBodyLock() {
    var nativeDialogOpen = document.querySelector('dialog[open]');
    var managedDialogOpen = document.querySelector(
      '[data-av5-dialog]:not(dialog)[role="dialog"][aria-modal="true"]:not([hidden])'
    );
    document.body.classList.toggle(
      'av5-dialog-open',
      !!(nativeDialogOpen || managedDialogOpen)
    );
  }

  function scheduleDialogBodyLockSync() {
    window.queueMicrotask(syncDialogBodyLock);
    window.requestAnimationFrame(syncDialogBodyLock);
  }

  function dialogRoot(name) {
    return document.querySelector('[data-av5-dialog="' + name + '"]');
  }

  function openDialog(name, opener) {
    var root = typeof name === 'string' ? dialogRoot(name) : name;
    if (!root || !root.hasAttribute('hidden')) return false;
    root.removeAttribute('hidden');
    if (!root.hasAttribute('role')) root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    var release = trapFocus(root);
    dialogStack.push({ root: root, opener: opener || null, release: release });
    document.body.classList.add('av5-dialog-open');
    bus.emit('ancona:v5:dialog-open', { name: root.getAttribute('data-av5-dialog') });
    return true;
  }

  function closeTopDialog() {
    var entry = dialogStack.pop();
    if (!entry) return false;
    entry.release();
    entry.root.setAttribute('hidden', '');
    scheduleDialogBodyLockSync();
    if (entry.opener && entry.opener.isConnected) entry.opener.focus();
    bus.emit('ancona:v5:dialog-close', { name: entry.root.getAttribute('data-av5-dialog') });
    return true;
  }

  function closeDialog(root) {
    for (var i = dialogStack.length - 1; i >= 0; i -= 1) {
      if (dialogStack[i].root === root) {
        while (dialogStack.length > i) closeTopDialog();
        return true;
      }
    }
    return false;
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    var openTrigger = event.target.closest('[data-av5-dialog-open]');
    if (openTrigger) {
      var opened = openDialog(openTrigger.getAttribute('data-av5-dialog-open'), openTrigger);
      if (opened) event.preventDefault();
      return;
    }
    var closeTrigger = event.target.closest('[data-av5-dialog-close]');
    if (closeTrigger) {
      var root = closeTrigger.closest('[data-av5-dialog]');
      if (root && closeDialog(root)) event.preventDefault();
      return;
    }
    /* Backdrop: the dialog root itself is the translucent overlay. */
    if (event.target instanceof Element && event.target.hasAttribute('data-av5-dialog')) {
      closeDialog(event.target);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && dialogStack.length) {
      event.preventDefault();
      closeTopDialog();
    }
  });

  /* ---- Client Book: deferred module loader + header badge ----------------- */
  var bookPromise = null;

  function clientBookUrl() {
    if (CLIENT_BOOK_SRC) return CLIENT_BOOK_SRC;
    if (!SCRIPT_SRC) return 'ancona-v5-client-book.js';
    return SCRIPT_SRC.replace(/ancona-v5-shell\.js/, 'ancona-v5-client-book.js');
  }

  /* Deferred load via an injected classic <script> tag — NOT dynamic import().
     Shopify's asset pipeline rewrites import() into a CommonJS require(), which
     throws "require is not defined" at runtime (measured on the V5 draft). The
     Client Book module self-boots and publishes window.AnconaClientBook, so a
     plain script tag gives the same deferred, non-blocking behavior. */
  function loadClientBook() {
    if (!bookPromise) {
      bookPromise = new Promise(function (resolve, reject) {
        if (window.AnconaClientBook) {
          resolve(window.AnconaClientBook);
          return;
        }
        var tag = document.createElement('script');
        tag.src = clientBookUrl();
        tag.defer = true;
        tag.setAttribute('data-av5-client-book', '');
        tag.onload = function () {
          bus.emit('ancona:v5:book-loaded', {});
          resolve(window.AnconaClientBook || null);
        };
        tag.onerror = function () {
          bookPromise = null; /* allow retry on next interaction */
          if (tag.parentNode) tag.parentNode.removeChild(tag);
          if (window.console && console.error) console.error('Ancona V5: Client Book module failed to load');
          reject(new Error('client-book-load-failed'));
        };
        document.head.appendChild(tag);
      });
    }
    return bookPromise;
  }

  var conciergePromise = null;
  var conciergeCssPromise = null;
  var conciergeMarkupPromise = null;

  function setConciergeLoadState(state) {
    var launchers = document.querySelectorAll('[data-av5-concierge-open]');
    for (var index = 0; index < launchers.length; index += 1) {
      launchers[index].dataset.av5LoadState = state;
      if (state === 'loading') launchers[index].setAttribute('aria-busy', 'true');
      else launchers[index].removeAttribute('aria-busy');
      if (state === 'ready') launchers[index].setAttribute('aria-controls', 'AnconaV5Concierge');
      else launchers[index].removeAttribute('aria-controls');
    }
  }

  function conciergeUrl() {
    if (CONCIERGE_SRC) return CONCIERGE_SRC;
    if (!SCRIPT_SRC) return 'ancona-concierge-core.js';
    return SCRIPT_SRC.replace(/ancona-v5-shell\.js/, 'ancona-concierge-core.js');
  }

  function conciergeCssUrl() {
    if (CONCIERGE_CSS) return CONCIERGE_CSS;
    if (!SCRIPT_SRC) return 'ancona-v5-concierge.css';
    return SCRIPT_SRC.replace(/ancona-v5-shell\.js/, 'ancona-v5-concierge.css');
  }

  function conciergeSectionUrl(host) {
    var sectionId = host && host.dataset
      ? String(host.dataset.av5ConciergeSectionId || '').trim()
      : '';
    if (!/^[a-z0-9][a-z0-9_-]{0,79}$/i.test(sectionId)) return '';
    var localeRoot = window.Shopify &&
      window.Shopify.routes &&
      typeof window.Shopify.routes.root === 'string'
      ? window.Shopify.routes.root
      : '/';
    var url = new URL(localeRoot, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    /* Keep draft previews on the same preview theme without copying arbitrary
       storefront query parameters into the section request. */
    var currentUrl = new URL(window.location.href);
    var previewThemeId = currentUrl.searchParams.get('preview_theme_id') || '';
    if (/^\d+$/.test(previewThemeId)) {
      url.searchParams.set('preview_theme_id', previewThemeId);
    }
    return url.toString();
  }

  function syncConciergeProductContext(root) {
    if (!root) return root;
    var launcher = document.querySelector(
      '.av5-concierge-launcher[data-av5-product-id][data-av5-product-handle][data-av5-product-title]'
    );
    if (!launcher) return root;
    var id = cleanProductId(launcher.dataset.av5ProductId);
    var handle = String(launcher.dataset.av5ProductHandle || '').trim();
    var title = String(launcher.dataset.av5ProductTitle || '').trim().slice(0, 180);
    if (!id || !/^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle) || !title) return root;
    root.dataset.currentProductId = id;
    root.dataset.currentProductHandle = handle;
    root.dataset.currentProductTitle = title;
    return root;
  }

  function loadConciergeMarkup() {
    var existingRoot = document.querySelector('[data-av5-concierge-root]');
    if (existingRoot) return Promise.resolve(syncConciergeProductContext(existingRoot));
    if (!conciergeMarkupPromise) {
      var host = document.querySelector('[data-av5-concierge-host]');
      var url = conciergeSectionUrl(host);
      if (!host || !url || typeof window.fetch !== 'function') {
        return Promise.reject(new Error('concierge-section-unavailable'));
      }
      conciergeMarkupPromise = window.fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (response) {
            if (!response.ok) throw new Error('concierge-section-http-' + response.status);
            return response.text();
          })
          .then(function (html) {
            var source = String(html || '').trim();
            if (!source || source === 'null') throw new Error('concierge-section-empty');
            var range = document.createRange();
            range.selectNode(host);
            var fragment = range.createContextualFragment(source);
            var root = fragment.querySelector('[data-av5-concierge-root]');
            if (!root) throw new Error('concierge-section-root-missing');
            host.replaceChildren(fragment);
            return syncConciergeProductContext(
              host.querySelector('[data-av5-concierge-root]')
            );
          })
          .catch(function (error) {
            conciergeMarkupPromise = null;
            host.replaceChildren();
            throw error;
          });
    }
    return conciergeMarkupPromise;
  }

  function loadConciergeCss() {
    if (!conciergeCssPromise) {
      conciergeCssPromise = new Promise(function (resolve, reject) {
        var href = conciergeCssUrl();
        if (!href) {
          resolve();
          return;
        }
        var existing = document.querySelector('link[data-av5-concierge-css]');
        if (existing) {
          if (existing.sheet) resolve();
          else {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', reject, { once: true });
          }
          return;
        }
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-av5-concierge-css', '');
        link.onload = resolve;
        link.onerror = function () {
          conciergeCssPromise = null;
          if (link.parentNode) link.parentNode.removeChild(link);
          reject(new Error('concierge-css-load-failed'));
        };
        document.head.appendChild(link);
      });
    }
    return conciergeCssPromise;
  }

  function loadConcierge() {
    if (!conciergePromise) {
      setConciergeLoadState('loading');
      conciergePromise = Promise.all([loadConciergeMarkup(), loadConciergeCss()])
        .then(function () {
          return new Promise(function (resolve, reject) {
            if (window.AnconaConcierge) {
              resolve(window.AnconaConcierge);
              return;
            }
            var tag = document.createElement('script');
            tag.src = conciergeUrl();
            tag.defer = true;
            tag.setAttribute('data-av5-concierge', '');
            tag.onload = function () {
              resolve(window.AnconaConcierge || null);
            };
            tag.onerror = function () {
              if (tag.parentNode) tag.parentNode.removeChild(tag);
              reject(new Error('concierge-load-failed'));
            };
            document.head.appendChild(tag);
          });
        })
        .then(function (api) {
          if (!api) throw new Error('concierge-api-unavailable');
          setConciergeLoadState('ready');
          bus.emit('ancona:v5:concierge-loaded', {});
          return api;
        })
        .catch(function (error) {
          setConciergeLoadState('error');
          conciergePromise = null;
          throw error;
        });
    }
    return conciergePromise;
  }

  function conciergeTrigger(event) {
    return event.target instanceof Element && event.target.closest('[data-av5-concierge-open]');
  }

  var conciergePrewarmTimer = 0;

  function scheduleConciergePrewarm() {
    window.clearTimeout(conciergePrewarmTimer);
    conciergePrewarmTimer = window.setTimeout(function () {
      loadConcierge().catch(function () {
        /* The normal click path owns retry and its contact-page fallback. */
      });
    }, 120);
  }

  function cancelConciergePrewarm() {
    window.clearTimeout(conciergePrewarmTimer);
    conciergePrewarmTimer = 0;
  }

  if (!window.matchMedia || window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('pointerover', function (event) {
      var trigger = conciergeTrigger(event);
      if (!trigger || (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))) return;
      scheduleConciergePrewarm();
    }, { passive: true });
    document.addEventListener('pointerout', function (event) {
      var trigger = conciergeTrigger(event);
      if (!trigger || (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))) return;
      cancelConciergePrewarm();
    }, { passive: true });
  }
  document.addEventListener('focusin', function (event) {
    if (conciergeTrigger(event)) scheduleConciergePrewarm();
  });
  document.addEventListener('focusout', function (event) {
    if (conciergeTrigger(event)) cancelConciergePrewarm();
  });

  function closeClientBookForConciergeHandoff(trigger) {
    if (
      !trigger ||
      typeof trigger.closest !== 'function' ||
      !trigger.closest('[data-av5-book-root]')
    ) return;
    var clientBook = window.AnconaClientBook;
    if (clientBook && typeof clientBook.close === 'function') {
      clientBook.close({ restoreFocus: false });
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = conciergeTrigger(event);
    if (!trigger) return;
    event.preventDefault();
    loadConcierge()
      .then(function (api) {
        if (api && typeof api.open === 'function') {
          closeClientBookForConciergeHandoff(trigger);
          api.open(trigger);
          return;
        }
        throw new Error('concierge-api-unavailable');
      })
      .catch(function () {
        announce(SHELL_S.guideUnavailableContact, 'polite');
        var launcher = document.querySelector('.av5-concierge-launcher[href]');
        var href = trigger.getAttribute('href') || (launcher && launcher.getAttribute('href'));
        if (href) window.location.assign(href);
      });
  });

  function readStoredBook() {
    try {
      var raw = window.localStorage.getItem(BOOK_KEY);
      if (!raw || raw.length > 200000) return null;
      var data = JSON.parse(raw);
      if (!data || data.v !== 2 || !Array.isArray(data.saved)) return null;
      return data;
    } catch (error) {
      return null;
    }
  }

  function readBookCount() {
    try {
      var data = readStoredBook();
      if (!data) return 0;
      var ids = data.ids && typeof data.ids === 'object' ? data.ids : {};
      var count = 0;
      for (var i = 0; i < data.saved.length; i += 1) {
        var entry = data.saved[i];
        if (!entry || typeof entry !== 'object') continue;
        var handle = typeof entry.h === 'string' ? entry.h : '';
        var productId = entry.pid || entry.productId || ids[handle] || '';
        if (presentationPolicy.allows(productId)) count += 1;
      }
      return count;
    } catch (error) {
      return 0; /* storage denied or malformed: badge stays hidden */
    }
  }

  /* Paint persisted Save state before the deferred Client Book bundle arrives.
     This prevents a cold “Save” control from silently acting as “Remove.” The
     module revalidates and takes over once loaded; this is a schema-bounded
     first-paint mirror only. */
  function syncColdBookButtons(scope) {
    if (window.AnconaClientBook) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    var controls = root.querySelectorAll('[data-av5-book]');
    var data = readStoredBook();
    var saved = data && Array.isArray(data.saved) ? data.saved : [];
    var ids = data && data.ids && typeof data.ids === 'object' ? data.ids : {};

    for (var index = 0; index < controls.length; index += 1) {
      var control = controls[index];
      var handle = String(control.dataset.av5Handle || '');
      var variantId = String(control.dataset.av5VariantId || '');
      var productId = cleanProductId(control.dataset.av5ProductId);
      var pressed = false;

      for (var savedIndex = 0; savedIndex < saved.length; savedIndex += 1) {
        var entry = saved[savedIndex];
        if (!entry || typeof entry !== 'object' || String(entry.h || '') !== handle) continue;
        var entryProductId = cleanProductId(entry.pid || entry.productId || ids[handle]);
        var entryVariantId = String(entry.vid || entry.variantId || '');
        if (
          productId &&
          entryProductId === productId &&
          (!variantId || entryVariantId === variantId || entryVariantId === '')
        ) {
          pressed = true;
          break;
        }
      }

      control.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      control.classList.toggle('is-saved', pressed);
      var label = control.querySelector('[data-av5-book-label]');
      var labelText = pressed
        ? (control.dataset.av5LabelSaved || (label && label.textContent) || 'Saved')
        : (control.dataset.av5LabelSave || (label && label.textContent) || 'Save');
      if (label) {
        label.textContent = labelText;
      }
      var title = String(control.dataset.av5Title || '').trim();
      control.setAttribute('aria-label', title ? labelText + ' — ' + title : labelText);
    }
  }

  /* Dynamic Ancona Edit cards are stamped from a template. Copy the canonical
     product identity already assigned to their adjacent Save control, so the
     Edit does not need a second product-data contract. */
  function hydrateCompareControls(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var controls = root.querySelectorAll('[data-av8-compare-from-book]');
    for (var index = 0; index < controls.length; index += 1) {
      var control = controls[index];
      var card = control.closest('[data-edit-card]') || control.parentElement;
      var source = card && card.querySelector('[data-av5-book]');
      if (!source) continue;
      control.dataset.handle = String(source.dataset.av5Handle || '');
      control.dataset.productId = String(source.dataset.av5ProductId || '');
      control.dataset.productTitle = String(source.dataset.av5Title || '');
      if (source.getAttribute('href')) control.setAttribute('href', source.getAttribute('href'));
    }
  }

  function storedCompareHandles() {
    var data = readStoredBook();
    if (!data || !Array.isArray(data.compare)) return [];
    var ids = data.ids && typeof data.ids === 'object' ? data.ids : {};
    var handles = [];
    for (var index = 0; index < data.compare.length && handles.length < 4; index += 1) {
      var handle = String(data.compare[index] || '').toLowerCase();
      var productId = cleanProductId(ids[handle]);
      if (!/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) continue;
      if (!presentationPolicy.allows(productId) || handles.indexOf(handle) !== -1) continue;
      handles.push(handle);
    }
    return handles;
  }

  function compareHandlesFromApi() {
    try {
      if (window.AnconaClientBook && typeof window.AnconaClientBook.compare === 'function') {
        var handles = window.AnconaClientBook.compare();
        if (Array.isArray(handles)) return handles.slice(0, 4);
      }
    } catch (error) { /* fall back to the schema-bounded storage mirror */ }
    return null;
  }

  function setColdCompareState(control, pressed) {
    var addLabel = control.dataset.av8LabelAdd || 'Place on Private Tray';
    var removeLabel = control.dataset.av8LabelRemove || 'Remove from Private Tray';
    var title = String(control.dataset.productTitle || '').trim();
    var labelText = pressed ? removeLabel : addLabel;
    control.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    control.classList.toggle('is-on-tray', pressed);
    var label = control.querySelector('[data-av8-compare-label]');
    if (label) label.textContent = labelText;
    control.setAttribute('aria-label', title ? labelText + ' — ' + title : labelText);
  }

  /* Mirror the persisted Private Tray state before the deferred Client Book
     module arrives. Controls remain hidden with no JavaScript; the shell only
     reveals eligible controls after their identity contract is complete. */
  function syncColdCompareButtons(scope) {
    hydrateCompareControls(scope);
    if (window.AnconaClientBook) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    var controls = root.querySelectorAll('[data-av5-compare-toggle]');
    var handles = storedCompareHandles();
    for (var index = 0; index < controls.length; index += 1) {
      var control = controls[index];
      var handle = String(control.dataset.handle || '').toLowerCase();
      var productId = cleanProductId(control.dataset.productId);
      var eligible = !!handle && presentationPolicy.allows(productId);
      if (!eligible) {
        control.setAttribute('hidden', '');
        continue;
      }
      control.removeAttribute('hidden');
      setColdCompareState(control, handles.indexOf(handle) !== -1);
    }
  }

  function syncPrivateTray() {
    var tray = document.querySelector('[data-av8-private-tray]');
    if (!tray) return;
    var apiHandles = compareHandlesFromApi();
    var handles = apiHandles === null ? storedCompareHandles() : apiHandles;
    var count = Math.min(4, handles.length);
    var previous = Number(tray.dataset.av8Count || 0);
    var hydrated = tray.dataset.av8Hydrated === 'true';
    tray.dataset.av8Count = String(count);
    tray.hidden = count === 0;
    document.documentElement.classList.toggle('av8-private-tray-visible', count > 0);

    var countNode = tray.querySelector('[data-av8-tray-count]');
    if (countNode) countNode.textContent = String(count);
    var slots = tray.querySelectorAll('[data-av8-tray-slot]');
    for (var index = 0; index < slots.length; index += 1) {
      slots[index].classList.toggle('is-filled', index < count);
    }
    var title = tray.dataset.av8Title || 'Private Tray';
    var open = tray.querySelector('[data-av5-book-open]');
    if (open) open.setAttribute('aria-label', title + ', ' + count + ' / 4');
    var status = tray.querySelector('[data-av8-tray-status]');
    if (status && hydrated && previous !== count) {
      status.textContent = count ? title + ': ' + count + ' / 4' : '';
    }
    tray.dataset.av8Hydrated = 'true';
    requestMobileLauncherPlacement();
  }

  function updateBookBadges(count) {
    var value = typeof count === 'number' ? count : readBookCount();
    var badges = document.querySelectorAll('[data-av5-book-count]');
    for (var i = 0; i < badges.length; i += 1) {
      badges[i].textContent = value > 99 ? '99+' : String(value);
      if (value > 0) badges[i].removeAttribute('hidden');
      else badges[i].setAttribute('hidden', '');
    }
  }

  function bookCountFromApi() {
    try {
      if (window.AnconaClientBook && typeof window.AnconaClientBook.list === 'function') {
        var list = window.AnconaClientBook.list();
        if (Array.isArray(list)) return list.length;
      }
    } catch (error) { /* fall back to storage read */ }
    return null;
  }

  document.addEventListener('ancona:v5:book-change', function () {
    var apiCount = bookCountFromApi();
    updateBookBadges(apiCount === null ? undefined : apiCount);
    syncPrivateTray();
  });

  window.addEventListener('storage', function (event) {
    if (event && event.key === BOOK_KEY) {
      updateBookBadges();
      syncColdBookButtons(document);
      syncColdCompareButtons(document);
      syncPrivateTray();
    }
  });
  document.addEventListener('ancona:v5:variant-context', function () {
    syncColdBookButtons(document);
    syncColdCompareButtons(document);
  });
  document.addEventListener('ancona:v5:grid-rendered', function () {
    syncColdBookButtons(document);
    syncColdCompareButtons(document);
  });
  document.addEventListener('ancona:v5:product-swapped', function (event) {
    var root = event.detail && event.detail.root;
    if (!root || !root.querySelectorAll) return;
    applyPresentationPolicy(root);
    syncColdBookButtons(root);
    syncColdCompareButtons(root);
    trackRecentContext(root);
  });

  /* First interaction with any Client Book control loads the module early
     (pointerdown fires before click, hiding the import latency). */
  document.addEventListener('pointerdown', function (event) {
    if (!(event.target instanceof Element)) return;
    var control = event.target.closest(
      '[data-av5-book], [data-av5-book-open], [data-av5-compare], [data-av5-compare-toggle]'
    );
    if (!control) return;
    if (control.hasAttribute('data-av5-book') && !presentationPolicy.allows(control.dataset.av5ProductId)) return;
    if (control.hasAttribute('data-av5-compare-toggle') && !presentationPolicy.allows(control.dataset.productId)) return;
    loadClientBook().catch(function () {});
  }, { capture: true, passive: true });

  document.addEventListener('keydown', function (event) {
    if (event.key !== ' ' || window.AnconaClientBook) return;
    var trigger = event.target instanceof Element && event.target.closest(
      'a[role="button"][data-av5-book], a[role="button"][data-av5-compare-toggle]'
    );
    if (!trigger) return;
    event.preventDefault();
    trigger.click();
  });

  /* Preserve the first cold Save click while the deferred module loads. */
  document.addEventListener('click', function (event) {
    var trigger = event.target instanceof Element && event.target.closest('[data-av5-book]');
    if (!trigger) return;
    event.preventDefault();
    event.anconaV5BookToggleHandled = true;
    var desiredPressed = trigger.getAttribute('aria-pressed') !== 'true';
    if (
      trigger.hasAttribute('data-av5-invalid-variant') ||
      trigger.getAttribute('aria-disabled') === 'true' ||
      !presentationPolicy.allows(trigger.dataset.av5ProductId)
    ) return;
    loadClientBook()
      .then(function (api) {
        if (!api || typeof api.toggle !== 'function') return;
        if (typeof api.isSaved === 'function') {
          var actualPressed = api.isSaved(trigger.dataset.av5Handle, trigger.dataset.av5VariantId || '');
          if (actualPressed === desiredPressed) return;
        }
        api.toggle(trigger);
      })
      .catch(function () {
        announce(SHELL_S.bookUnavailable, 'polite');
      });
  });

  /* Preserve the first cold Private Tray click while its owner module loads.
     The desired aria-pressed state is captured before the asynchronous load,
     avoiding a lost or double toggle on quick taps. */
  document.addEventListener('click', function (event) {
    if (window.AnconaClientBook) return;
    var trigger = event.target instanceof Element && event.target.closest('[data-av5-compare-toggle]');
    if (!trigger) return;
    event.preventDefault();
    event.anconaV5CompareToggleHandled = true;
    var handle = String(trigger.dataset.handle || '');
    var productId = cleanProductId(trigger.dataset.productId);
    var desiredPressed = trigger.getAttribute('aria-pressed') !== 'true';
    if (!handle || !presentationPolicy.allows(productId)) return;
    trigger.dataset.busy = 'true';
    trigger.setAttribute('aria-busy', 'true');
    loadClientBook()
      .then(function (api) {
        if (!api || typeof api.setCompare !== 'function') throw new Error('client-book-api-unavailable');
        api.setCompare({ h: handle, pid: productId }, '', desiredPressed);
      })
      .catch(function () {
        announce(SHELL_S.bookUnavailable, 'polite');
      })
      .finally(function () {
        trigger.dataset.busy = 'false';
        trigger.removeAttribute('aria-busy');
      });
  });

  /* The header trigger opens the Client Book drawer once the module is in. */
  document.addEventListener('click', function (event) {
    var trigger = event.target instanceof Element && event.target.closest('[data-av5-book-open]');
    if (!trigger) return;
    event.preventDefault();
    event.anconaV5BookOpenHandled = true;
    loadClientBook()
      .then(function (api) {
        if (api && typeof api.open === 'function') {
          api.open(trigger);
          return;
        }
        bus.emit('ancona:v5:book-open', { source: 'header', opener: trigger });
      })
      .catch(function () {
        announce(SHELL_S.bookUnavailable, 'polite');
      });
  });

  /* ---- Metrics: explicit analytics consent; local book events excluded --- */
  /* Custom metrics require an explicit analytics choice and current permission.
     Missing/late privacy APIs drop this event; nothing is queued for replay. */
  function anconaAnalyticsConsentAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.currentVisitorConsent !== 'function' ||
          typeof privacy.analyticsProcessingAllowed !== 'function') return false;
      var consent = privacy.currentVisitorConsent();
      return !!(consent && consent.analytics === 'yes' &&
        privacy.analyticsProcessingAllowed() === true);
    } catch (error) {
      return false;
    }
  }

  try { window.dataLayer = window.dataLayer || []; } catch (error) { /* never break the page */ }

  var METRIC_EVENTS = [
    /* V4 vocabulary (ancona-metrics.js) — names unchanged */
    'saved_add', 'saved_remove', 'shortlist_share', 'return_to_results',
    'recently_viewed_clear', 'guide_toc_click',
    'saved_open', 'compare_add', 'compare_remove', 'compare_open',
    'inquiry_start', 'inquiry_context',
    'product_print', 'guide_print', 'guide_share',
    'sticky_add_to_cart', 'cta_click', 'recs_fallback_shown',
    'saved_cart_add', 'saved_refresh', 'shortlist_view', 'saved_update'
    /* Client Book open/change carry local UI state and are never republished. */
  ];

  METRIC_EVENTS.forEach(function (name) {
    document.addEventListener('ancona:' + name, function (event) {
      try {
        var detail = event && event.detail ? event.detail : {};
        /* Cross-tab mirror events are UI refresh signals, not shopper KPIs. */
        if (detail.source === 'tabsync' || detail.external === true) return;
        if (!anconaAnalyticsConsentAllowed()) return;
        if (!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function')) return;
        window.Shopify.analytics.publish('ancona:' + name, detail);
      } catch (error) { /* analytics must never break shopping */ }
    });
  });

  /* ---- Product-aware contact handoff ------------------------------------- */
  function cleanContactContext(value, limit) {
    return String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, limit || 300);
  }

  function applyContactProductContext() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }
    var handles = [];
    var primaryHandle = cleanRecentHandle(params.get('product'));
    if (primaryHandle) handles.push(primaryHandle);
    String(params.get('products') || '').split(',').slice(0, 4).forEach(function (value) {
      var handle = cleanRecentHandle(value);
      if (handle && handles.indexOf(handle) === -1 && handles.length < 4) handles.push(handle);
    });
    var question = cleanContactContext(params.get('question'), 300);
    var request = cleanContactContext(params.get('request'), 300);
    var listedFacts = cleanContactContext(params.get('listed_facts'), 500);
    if (!handles.length && !question && !request && !listedFacts) return;

    var forms = document.querySelectorAll('form[action*="/contact"]');
    var contactForm = null;
    var questionField = null;
    for (var formIndex = 0; formIndex < forms.length; formIndex += 1) {
      questionField = forms[formIndex].querySelector('textarea[name^="contact["]');
      if (questionField) {
        contactForm = forms[formIndex];
        break;
      }
    }
    if (!contactForm || contactForm.dataset.av5ProductContextState) return;
    contactForm.dataset.av5ProductContextState = handles.length ? 'loading' : 'context';

    function appendHidden(key, name, value) {
      if (!value) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.dataset.av5ContactContextField = key;
      contactForm.appendChild(input);
    }

    if (question) appendHidden('question', 'contact[Concierge question]', question);
    if (request) appendHidden('request', 'contact[Concierge request]', request);
    if (listedFacts) {
      appendHidden(
        'listed-facts',
        'contact[Listed product facts — verify]',
        listedFacts
      );
    }

    var visiblePrefill = [];
    if (question) visiblePrefill.push(question);
    if (request && request !== question) visiblePrefill.push(SHELL_S.prefillRequest + request);
    if (listedFacts) visiblePrefill.push(SHELL_S.prefillFacts + listedFacts);
    if (!questionField.value.trim() && visiblePrefill.length) {
      questionField.value = visiblePrefill.join('\n\n').slice(0, 1800);
    }

    var storefrontRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    var controller = new AbortController();
    var timeoutId = window.setTimeout(function () { controller.abort(); }, 8000);

    function verifyHandle(handle) {
      var productUrl = new URL(
        String(storefrontRoot).replace(/\/?$/, '/') + 'products/' + handle,
        window.location.origin
      );
      var productEndpoint = new URL(productUrl.href);
      productEndpoint.pathname += '.js';
      return fetch(productEndpoint.href, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'error',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      }).then(function (response) {
        if (!response.ok) throw new Error('product-context-request-failed');
        var contentLength = Number(response.headers.get('content-length') || 0);
        if (contentLength > 2000000) throw new Error('product-context-response-too-large');
        return response.text().then(function (text) {
          if (text.length > 2000000) throw new Error('product-context-response-too-large');
          return JSON.parse(text);
        });
      }).then(function (productData) {
        var verifiedId = cleanProductId(productData && productData.id);
        var verifiedHandle = cleanRecentHandle(productData && productData.handle);
        var verifiedTitle = cleanContactContext(productData && productData.title, 180);
        if (
          !verifiedId ||
          verifiedHandle !== handle ||
          !verifiedTitle ||
          !presentationPolicy.allows(verifiedId)
        ) throw new Error('product-context-verification-failed');
        return {
          id: verifiedId,
          handle: verifiedHandle,
          title: verifiedTitle,
          url: new URL(
            String(storefrontRoot).replace(/\/?$/, '/') + 'products/' + verifiedHandle,
            window.location.origin
          )
        };
      });
    }

    var verification = handles.length
      ? Promise.all(handles.map(function (handle) {
          return verifyHandle(handle).catch(function () { return null; });
        }))
      : Promise.resolve([]);

    verification.then(function (results) {
      var verifiedProducts = results.filter(Boolean);
      verifiedProducts.forEach(function (product, index) {
        var suffix = verifiedProducts.length === 1 ? '' : ' ' + (index + 1);
        appendHidden(
          'product-title-' + index,
          'contact[Product' + suffix + ' title]',
          product.title
        );
        appendHidden(
          'product-handle-' + index,
          'contact[Product' + suffix + ' handle]',
          product.handle
        );
        appendHidden(
          'product-url-' + index,
          'contact[Product' + suffix + ' URL]',
          product.url.href
        );
      });
      appendHidden(
        'inquiry-type',
        'contact[Concierge inquiry type]',
        verifiedProducts.length ? 'Product question' : 'Concierge request'
      );
      appendHidden(
        'concierge-context',
        'contact[Concierge context]',
        verifiedProducts.length > 1 ? 'Ask about these pieces' : 'Ask about this piece'
      );

      var notice = document.createElement('p');
      notice.id = 'AnconaContactProductContext';
      notice.className = 'av5-contact-product-context';
      notice.setAttribute('role', 'status');
      if (verifiedProducts.length) {
        notice.appendChild(document.createTextNode(SHELL_S.contextIncludes));
        verifiedProducts.forEach(function (product, index) {
          if (index) notice.appendChild(document.createTextNode(index === verifiedProducts.length - 1 ? SHELL_S.contextAnd : SHELL_S.contextComma));
          var productLink = document.createElement('a');
          productLink.href = product.url.pathname;
          productLink.textContent = product.title;
          notice.appendChild(productLink);
        });
        notice.appendChild(document.createTextNode(
          SHELL_S.contextReview
        ));
        if (verifiedProducts.length < handles.length) {
          notice.appendChild(document.createTextNode(
            SHELL_S.contextPartial
          ));
        }
      } else if (handles.length) {
        notice.textContent =
          SHELL_S.contextFailed;
      } else {
        notice.textContent =
          SHELL_S.contextReady;
      }
      contactForm.insertBefore(notice, contactForm.firstChild);
      if (!questionField.getAttribute('placeholder')) {
        questionField.setAttribute('placeholder', SHELL_S.questionPlaceholder);
      }
      var describedBy = String(questionField.getAttribute('aria-describedby') || '').trim();
      questionField.setAttribute(
        'aria-describedby',
        (describedBy ? describedBy + ' ' : '') + notice.id
      );
      questionField.dataset.av5ProductQuestion = verifiedProducts.length ? 'true' : 'false';
      contactForm.dataset.av5ProductContextState =
        verifiedProducts.length === handles.length ? 'verified' : (verifiedProducts.length ? 'partial' : 'context');
    }).catch(function () {
      contactForm.dataset.av5ProductContextState = 'unavailable';
      var notice = document.createElement('p');
      notice.className = 'av5-contact-product-context';
      notice.setAttribute('role', 'status');
      notice.textContent =
        SHELL_S.contextError;
      contactForm.insertBefore(notice, contactForm.firstChild);
    }).finally(function () {
      window.clearTimeout(timeoutId);
    });
  }

  /* Keep the compact mobile launcher away from visible purchase/navigation
     controls. This is an event-driven, bounded placement check: no polling,
     MutationObserver, or page-wide layout scan. */
  var launcherCollisionFrame = 0;
  var launcherCollisionBound = false;
  var launcherCollisionTimer = 0;
  var launcherScrollBound = false;
  var launcherMobileMedia = null;
  var FLOATING_LAUNCHERS = '.av5-concierge-launcher, .av5-client-book-launcher';
  var LAUNCHER_CRITICAL =
    '.product-form__submit, .shopify-payment-button, .shopify-payment-button__button, ' +
    'shopify-accelerated-checkout, shopify-buy-it-now-button, ' +
    '.cart__dynamic-checkout-buttons, #dynamic-checkout-cart, [data-shopify-buttoncontainer], ' +
    '.product__info-container square-placement[data-platform="shopify-app-blocks"], ' +
    '[data-av5-sticky-atc], cart-drawer.active, cart-drawer[open], ' +
    'cart-notification[open], #shopify-pc__banner, #shopify-pc__prefs, ' +
    '.shopify-pc__banner__dialog, #shop-hcaptcha-badge-container, ' +
    '[data-av5-book-root][open], [data-av8-private-tray]:not([hidden]), ' +
    '[data-ancona-mux-nav], ' +
    'dialog[open]:not(.av5-concierge)';

  function rectsOverlap(first, second) {
    return !(
      first.right <= second.left ||
      first.left >= second.right ||
      first.bottom <= second.top ||
      first.top >= second.bottom
    );
  }

  function launcherCandidateRect(name, width, height) {
    var edge = 12;
    /* Match the mobile CSS raised position. The 92px offset clears the
       78px sticky purchase bar with a deliberate visual/touch separation. */
    var raised = 92;
    var isProduct = document.body.classList.contains('template-product');
    var isCart = document.body.classList.contains('template-cart');
    /* Product pages reserve the sticky purchase bar from first paint. The
       sticky bar changes visibility after its observer runs, so waiting for a
       scroll/resize event can briefly leave the launcher overlapping it. */
    var defaultBottom = isProduct ? raised : (isCart ? 72 : edge);
    /* Atelier 360 pass 2: on pages with the fixed mobile bottom nav the CSS
       lifts every launcher by the nav height + 14px. Mirror that in the
       candidate rects so overlap counting matches the rendered position. */
    var muxNav = document.querySelector('[data-ancona-mux-nav]');
    if (muxNav && muxNav.offsetHeight > 0) defaultBottom += muxNav.offsetHeight + 14;
    var left = /left/.test(name) ? edge : window.innerWidth - edge - width;
    var top;
    if (/middle/.test(name)) top = (window.innerHeight - height) / 2;
    else {
      var bottom = /raised/.test(name) ? Math.max(raised, defaultBottom) : defaultBottom;
      top = window.innerHeight - bottom - height;
    }
    return {
      left: left,
      top: top,
      right: left + width,
      bottom: top + height
    };
  }

  function setFloatingLaunchersSuppressed(suppressed) {
    var active = document.activeElement;
    Array.prototype.forEach.call(document.querySelectorAll(FLOATING_LAUNCHERS), function (control) {
      var hasFocus = active && (active === control || control.contains(active));

      /* Do not remove the focused control from the accessibility tree. A
         document-level focusout hook immediately retries suppression after
         focus moves elsewhere. */
      if (suppressed && hasFocus) {
        control.removeAttribute('data-av5-launcher-suppressed');
        control.setAttribute('data-av5-launcher-suppression-pending', 'true');
        return;
      }

      control.removeAttribute('data-av5-launcher-suppression-pending');
      if (suppressed) control.setAttribute('data-av5-launcher-suppressed', 'true');
      else control.removeAttribute('data-av5-launcher-suppressed');
    });
  }

  function placeMobileLauncher() {
    launcherCollisionFrame = 0;
    var launcher = document.querySelector('.av5-concierge-launcher');
    if (!launcher) return;
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    if (!mobile) launcher.removeAttribute('data-av5-launcher-placement');

    var launcherRect = launcher.getBoundingClientRect();
    if (!launcherRect.width || !launcherRect.height) return;
    var criticalRects = Array.prototype.slice.call(document.querySelectorAll(LAUNCHER_CRITICAL))
      .filter(function (element) {
        if (launcher.contains(element) || element.contains(launcher)) return false;
        var style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
      })
      .map(function (element) { return element.getBoundingClientRect(); });
    if (!mobile) {
      /* The existing suppression CSS uses visibility, not display, so this
         rectangle remains measurable while hidden and restores on clearance. */
      setFloatingLaunchersSuppressed(criticalRects.some(function (rect) {
        return rectsOverlap(launcherRect, rect);
      }));
      return;
    }
    var candidates = ['right', 'left', 'right-raised', 'left-raised', 'right-middle', 'left-middle'];
    var best = candidates[0];
    var bestCount = Number.POSITIVE_INFINITY;
    for (var index = 0; index < candidates.length; index += 1) {
      var candidateRect = launcherCandidateRect(
        candidates[index],
        launcherRect.width,
        launcherRect.height
      );
      var count = criticalRects.filter(function (rect) {
        return rectsOverlap(candidateRect, rect);
      }).length;
      if (count < bestCount) {
        best = candidates[index];
        bestCount = count;
      }
      if (count === 0) break;
    }
    if (launcher.dataset.av5LauncherPlacement !== best) {
      launcher.setAttribute('data-av5-launcher-placement', best);
    }
    setFloatingLaunchersSuppressed(bestCount > 0);
  }

  function requestMobileLauncherPlacement() {
    if (launcherCollisionFrame) return;
    launcherCollisionFrame = window.requestAnimationFrame(placeMobileLauncher);
  }

  function requestMobileLauncherPlacementTrailing() {
    window.clearTimeout(launcherCollisionTimer);
    launcherCollisionTimer = window.setTimeout(requestMobileLauncherPlacement, 120);
  }

  function syncMobileLauncherScrollBinding() {
    if (!launcherScrollBound) {
      window.addEventListener('scroll', requestMobileLauncherPlacementTrailing, { passive: true });
      launcherScrollBound = true;
    }
    requestMobileLauncherPlacement();
  }

  function bindMobileLauncherCollisionGuard() {
    if (launcherCollisionBound) {
      requestMobileLauncherPlacement();
      return;
    }
    launcherCollisionBound = true;
    window.addEventListener('resize', requestMobileLauncherPlacement, { passive: true });
    window.addEventListener('orientationchange', requestMobileLauncherPlacement, { passive: true });
    window.addEventListener('load', requestMobileLauncherPlacement, { once: true });
    document.addEventListener('ancona:v5:concierge-close', requestMobileLauncherPlacement);
    document.addEventListener('focusout', function (event) {
      var target = event.target;
      if (
        target &&
        target.closest &&
        target.closest('[data-av5-launcher-suppression-pending="true"]')
      ) {
        requestMobileLauncherPlacement();
      }
    });
    launcherMobileMedia = window.matchMedia('(max-width: 749px)');
    if (launcherMobileMedia.addEventListener) {
      launcherMobileMedia.addEventListener('change', syncMobileLauncherScrollBinding);
    } else if (launcherMobileMedia.addListener) {
      launcherMobileMedia.addListener(syncMobileLauncherScrollBinding);
    }
    syncMobileLauncherScrollBinding();
  }

  var sharedBookBooted = false;

  /* Preserve shared V1 shortlist URLs while keeping V5 as the only renderer.
     A shared-link visit is explicit Client Book intent, so this is the sole
     non-click path that loads the deferred module on an ordinary route. */
  function openSharedBookFromLocation() {
    if (sharedBookBooted) return;
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }
    var current = params.get('book') || '';
    var legacy = params.get('shortlist') || '';
    var raw = current || legacy;
    if (!raw) return;
    var handles = [];
    raw.split(',').slice(0, 50).forEach(function (value) {
      var handle = cleanRecentHandle(value);
      if (handle && handles.indexOf(handle) === -1) handles.push(handle);
    });
    if (!handles.length) return;
    sharedBookBooted = true;

    if (!current && legacy && window.history && typeof window.history.replaceState === 'function') {
      try {
        params.set('book', handles.join(','));
        params.delete('shortlist');
        var nextUrl = window.location.pathname +
          (params.toString() ? '?' + params.toString() : '') +
          window.location.hash;
        window.history.replaceState(window.history.state, '', nextUrl);
      } catch (error) {
        /* The Client Book module also reads shortlist= directly. */
      }
    }

    if (document.querySelector('[data-av5-book-root][data-context="page"]')) return;
    loadClientBook()
      .then(function (api) {
        if (!api || typeof api.open !== 'function') return;
        var opener = document.querySelector('[data-av5-book-open]');
        api.open(opener || null);
      })
      .catch(function () {
        announce(SHELL_S.sharedBookUnavailable, 'polite');
      });
  }

  /* ---- Boot (P0-8 pattern: DOMContentLoaded + shopify:section:load) ------- */
  function init() {
    applyPresentationPolicy(document);
    syncColdBookButtons(document);
    syncColdCompareButtons(document);
    syncPrivateTray();
    updateBookBadges();
    if (document.querySelector('[data-av5-book-root][data-context="page"]')) {
      loadClientBook().catch(function () {
        announce(SHELL_S.bookUnavailable, 'polite');
      });
    }
    if (document.querySelector('[data-av5-concierge-form-return]')) {
      loadConcierge().catch(function () {
        announce(SHELL_S.contactResponseNoGuide, 'polite');
      });
    }
    openSharedBookFromLocation();
    applyContactProductContext();
    scheduleRecentTracking();
    bindMobileLauncherCollisionGuard();
  }

  var recentTrackingScheduled = false;
  var MAX_RECENT = 12;

  function cleanRecentHandle(value) {
    var handle = String(value == null ? '' : value).trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : '';
  }

  function hasLegacyBookStorage() {
    try {
      return !!(
        window.localStorage.getItem('ancona:v1:saved') ||
        window.localStorage.getItem('ancona:v1:compare') ||
        window.localStorage.getItem('ancona:recently-viewed')
      );
    } catch (error) {
      return true;
    }
  }

  function recentBookSnapshot() {
    try {
      var raw = window.localStorage.getItem(BOOK_KEY);
      if (!raw) {
        if (hasLegacyBookStorage()) return null;
        return { v: 2, saved: [], compare: [], recent: [], ids: Object.create(null) };
      }
      if (raw.length > 200000) return null;
      var data = JSON.parse(raw);
      if (
        !data ||
        data.v !== 2 ||
        !Array.isArray(data.saved) ||
        !Array.isArray(data.compare) ||
        !Array.isArray(data.recent) ||
        data.saved.length > 50 ||
        data.compare.length > 4 ||
        data.recent.length > MAX_RECENT ||
        !data.ids ||
        typeof data.ids !== 'object' ||
        Array.isArray(data.ids)
      ) return null;

      for (var recentIndex = 0; recentIndex < data.recent.length; recentIndex += 1) {
        var entry = data.recent[recentIndex];
        if (
          !entry ||
          typeof entry !== 'object' ||
          !cleanRecentHandle(entry.h) ||
          !cleanProductId(entry.pid) ||
          !Number.isFinite(Number(entry.ts))
        ) return null;
      }

      var ids = Object.create(null);
      var idKeys = Object.keys(data.ids);
      if (idKeys.length > 100) return null;
      for (var idIndex = 0; idIndex < idKeys.length; idIndex += 1) {
        var key = cleanRecentHandle(idKeys[idIndex]);
        var productId = cleanProductId(data.ids[idKeys[idIndex]]);
        if (!key || !productId) return null;
        ids[key] = productId;
      }

      return {
        v: 2,
        saved: data.saved,
        compare: data.compare,
        recent: data.recent,
        ids: ids
      };
    } catch (error) {
      return null;
    }
  }

  function trackRecentContext(scope) {
    if (!privacyAllows('preferences') || !TRACK_RECENT || window.AnconaClientBook) return false;
    var target = scope && scope.querySelector ? scope : document;
    var contextNode = target.matches && target.matches('[data-av5-product-context]')
      ? target
      : target.querySelector('[data-av5-product-context]');
    if (!contextNode) return false;

    var context = {};
    try {
      context = JSON.parse(contextNode.textContent || '{}');
    } catch (error) {
      return false;
    }
    var handle = cleanRecentHandle(context.handle);
    var host = contextNode.closest('[data-product-id]');
    var productInfo = document.querySelector('product-info[data-product-id]');
    var productId = cleanProductId(
      context.productId ||
      context.pid ||
      (host && host.dataset.productId) ||
      (productInfo && productInfo.dataset.productId)
    );
    if (!handle || !presentationPolicy.allows(productId)) return false;

    var snapshot = recentBookSnapshot();
    if (!snapshot) return false;
    var alreadyRecent = (
      snapshot.recent[0] &&
      cleanRecentHandle(snapshot.recent[0].h) === handle &&
      cleanProductId(snapshot.recent[0].pid) === productId
    );
    if (alreadyRecent && cleanProductId(snapshot.ids[handle]) === productId) return false;
    if (!alreadyRecent) {
      var recent = [];
      for (var index = 0; index < snapshot.recent.length; index += 1) {
        if (cleanRecentHandle(snapshot.recent[index].h) !== handle) recent.push(snapshot.recent[index]);
      }
      recent.unshift({ h: handle, pid: productId, ts: Date.now() });
      snapshot.recent = recent.slice(0, MAX_RECENT);
    }
    snapshot.ids[handle] = productId;

    try {
      var serialized = JSON.stringify(snapshot);
      if (serialized.length > 200000) return false;
      window.localStorage.setItem(BOOK_KEY, serialized);
      updateBookBadges();
      bus.emit('ancona:v5:book-change', { source: 'recent', recentOnly: true });
      return true;
    } catch (error) {
      return false;
    }
  }

  function synchronizeRecentPrivacy() {
    if (!privacyAllows('preferences')) {
      clearStoredRecentHistory();
      return;
    }
    /* Record only this currently visible product after permission becomes known. */
    trackRecentContext(document);
  }
  document.addEventListener('visitorConsentCollected', collectPreferencesDecision);
  document.addEventListener('visitorConsentCollected', synchronizeRecentPrivacy);
  document.addEventListener('ancona:privacy-ready', synchronizeRecentPrivacy);
  window.addEventListener('storage', function (event) {
    if (event.key === PREFERENCES_DECISION_KEY || event.key === null) {
      volatilePreferencesDecision = null;
      synchronizeRecentPrivacy();
      document.dispatchEvent(new CustomEvent('ancona:preferences-decision'));
    } else if (event.key === BOOK_KEY || event.key === 'ancona:recently-viewed') {
      clearStoredRecentHistory();
    }
  });
  synchronizeRecentPrivacy();

  function scheduleRecentTracking() {
    if (!TRACK_RECENT || recentTrackingScheduled) return;
    recentTrackingScheduled = true;
    var track = function () { trackRecentContext(document); };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(track, { timeout: 1500 });
    } else {
      window.setTimeout(track, 800);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    /* Re-rendered header instances need their badge repainted; all other
       wiring is document-delegated and survives section reloads. */
    applyPresentationPolicy(document);
    syncColdBookButtons(document);
    syncColdCompareButtons(document);
    syncPrivateTray();
    updateBookBadges();
    if (document.querySelector('[data-av5-book-root][data-context="page"]')) {
      loadClientBook().catch(function () {});
    }
    applyContactProductContext();
    scheduleRecentTracking();
    requestMobileLauncherPlacement();
  });

  window.AnconaV5 = {
    bus: bus,
    trapFocus: trapFocus,
    announce: announce,
    dialog: {
      open: openDialog,
      close: closeDialog,
      closeTop: closeTopDialog,
      syncBodyLock: syncDialogBodyLock,
      scheduleBodyLockSync: scheduleDialogBodyLockSync
    },
    presentationPolicy: presentationPolicy,
    loadClientBook: loadClientBook,
    loadConcierge: loadConcierge,
    getClientBookCount: readBookCount,
    updateBookBadges: updateBookBadges
  };
})();
